import { Empty } from '@hendrx/icons/Empty';
import { Box, Flex, luminance, UnstyledButton } from '@mantine/core';
import cx from 'clsx';
import { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './ColorSwatch.module.scss';
import { getHexaSwatchColor } from './utils/getHexaSwatchColor';
import { ColorSwatchSize } from '../../../types/ColorPicker';
import { convertCssValueToHexaColor } from '../../../utils/convertCssValueToHexaColor';
import { isHexaColor } from '../../../utils/isHexaColor';
import { isVar } from '../../../utils/isVar';

type ColorSwatchProps = {
  color?: string | null;
  size?: ColorSwatchSize;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
};

export function ColorSwatch(props: ColorSwatchProps) {
  const { color, size = ColorSwatchSize.medium, selected = false, onClick, className } = props;

  const { t } = useTranslation('widgets.colorPicker');

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (onClick) {
        onClick();
      }
    }
  };

  if (!color) {
    return (
      <UnstyledButton onKeyDown={handleKeyDown} onClick={onClick} aria-label={t('title')}>
        <Flex
          className={cx(
            classes.wrapper,
            {
              [classes.large]: size === ColorSwatchSize.large,
              [classes.small]: size === ColorSwatchSize.small,
              [classes.selected]: selected
            },
            className
          )}
          tabIndex={0}
        >
          <Empty className={classes.empty} />
        </Flex>
      </UnstyledButton>
    );
  }

  const resolvedColor = isVar(color) ? convertCssValueToHexaColor(color) : color;
  const isHexa = isHexaColor(resolvedColor);
  const colorLuminance = luminance(resolvedColor);
  let colorToDisaply = resolvedColor;
  let withBorder = colorLuminance > 0.9;

  if (isHexa) {
    const { opacity, colorString } = getHexaSwatchColor(resolvedColor);
    colorToDisaply = colorString;
    if (!withBorder) {
      withBorder = opacity < 0.1;
    }
  }

  return (
    <UnstyledButton onKeyDown={handleKeyDown} onClick={onClick} aria-label={t('title')}>
      <Flex
        className={cx(
          classes.wrapper,
          {
            [classes.large]: size === ColorSwatchSize.large,
            [classes.small]: size === ColorSwatchSize.small,
            [classes.selected]: selected
          },
          className
        )}
      >
        <Box
          className={cx(classes.contentWrapper, {
            [classes.withBorder]: withBorder
          })}
        >
          <Box className={cx({ [classes.background]: isHexa })} />
          <Box className={classes.content} style={{ '--background': colorToDisaply }} />
        </Box>
      </Flex>
    </UnstyledButton>
  );
}
